import { useLayoutEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import Button from '@/components/ui/Button/Button';
import { logoutAdminSession } from '@/features/board/api';
import { clearAdminAccessToken, hasAdminAccessToken } from '@/features/board/auth';
import { createAdminCatalogSectionLinks } from '@/features/catalog/adminCatalogNavigation';
import { contentQueryKeys, fetchAdminCatalogCategories } from '@/features/content/api';
import styles from '@/pages/AdminBoardPage.module.scss';
import { restoreDocumentScrollTop } from '@/pages/RootLayout/rootLayoutScrollLock';
import { routePaths } from '@/routes/routeRegistry';

const AdminRouteLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const catalogCategoriesQuery = useQuery({
    queryKey: contentQueryKeys.adminCatalogCategories,
    queryFn: fetchAdminCatalogCategories,
    staleTime: 60_000,
  });
  const catalogSectionLinks = createAdminCatalogSectionLinks(
    (catalogCategoriesQuery.data ?? []).filter((category) => category.isVisible),
  );
  const navGroups = [
    {
      title: '제품·솔루션',
      items: [
        { label: '카테고리 관리', to: routePaths.adminCatalogCategories },
        ...catalogSectionLinks.map((link) => ({
          label: link.label,
          to: link.to,
        })),
      ],
    },
    {
      title: '정보지원',
      items: [
        { label: '언론보도', to: routePaths.adminMediaCoverages },
        { label: '시공사례', to: routePaths.adminConstructionCases },
        { label: '공사실적', to: routePaths.adminProjectRecords },
        { label: '기술자료', to: routePaths.adminSupportMaterials },
        { label: '웹카탈로그', to: routePaths.adminWebCatalogs },
        { label: '홍보영상', to: routePaths.adminPromoVideos },
      ],
    },
    {
      title: '회사소개',
      items: [
        { label: '조직도', to: routePaths.adminOrganization },
        { label: '연혁', to: routePaths.adminHistoryEntries },
        { label: '인증·특허', to: routePaths.adminCertificationAssets },
      ],
    },
    {
      title: '문의',
      items: [{ label: '문의접수', to: routePaths.adminInquiries }],
    },
    {
      title: '홈',
      items: [
        { label: '히어로 문구', to: routePaths.adminHomeHeroText },
        { label: '홈 주요 취급 설비', to: routePaths.adminHomeFeaturedEquipments },
        { label: '팝업', to: routePaths.adminPopups },
      ],
    },
  ];
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(navGroups.map((group) => [group.title, true])),
  );

  useLayoutEffect(() => {
    restoreDocumentScrollTop(0);
  }, [location.pathname, location.search]);

  if (!hasAdminAccessToken()) {
    return <Navigate replace state={{ from: location.pathname }} to={routePaths.adminLogin} />;
  }

  const handleLogout = () => {
    void logoutAdminSession().catch(() => undefined);
    clearAdminAccessToken();
    void navigate(routePaths.adminLogin, { replace: true });
  };

  const isSidebarItemActive = (to: string) => {
    const [pathname, search] = to.split('?');

    if (search) {
      return location.pathname === pathname && location.search === `?${search}`;
    }

    return location.pathname === pathname || location.pathname.startsWith(`${pathname}/`);
  };

  return (
    <div className={styles['shell']}>
      <aside className={styles['sidebar']}>
        <div className={styles['sidebarHeader']}>
          <p className={styles['eyebrow']}>KookjeTNC Admin</p>
          <h1 className={styles['sidebarTitle']}>운영 관리</h1>
        </div>

        <nav className={styles['sidebarNav']}>
          {navGroups.map((group) => {
            return (
              <details
                className={styles['sidebarGroup']}
                key={group.title}
                onToggle={(event) => {
                  const isOpen = event.currentTarget.open;
                  setOpenGroups((current) =>
                    current[group.title] === isOpen
                      ? current
                      : { ...current, [group.title]: isOpen },
                  );
                }}
                open={openGroups[group.title] ?? true}
              >
                <summary className={styles['sidebarGroupTitle']}>{group.title}</summary>
                <div className={styles['sidebarChildList']}>
                  {group.items.map((item) => {
                    return (
                      <NavLink
                        className={() =>
                          `${styles['sidebarChildLink']} ${
                            isSidebarItemActive(item.to) ? styles['sidebarLinkActive'] : ''
                          }`
                        }
                        key={item.to}
                        to={item.to}
                      >
                        {item.label}
                      </NavLink>
                    );
                  })}
                </div>
              </details>
            );
          })}
        </nav>

        <div className={styles['sidebarFooter']}>
          <Link className={styles['link']} to={routePaths.home}>
            홈페이지로 돌아가기
          </Link>
          <Button
            className={styles['adminSecondaryButton']}
            onClick={handleLogout}
            type='button'
            variant='secondary'
          >
            로그아웃
          </Button>
        </div>
      </aside>

      <section className={styles['contentArea']}>
        <div className={styles['page']}>
          <Outlet />
        </div>
      </section>
    </div>
  );
};

export default AdminRouteLayout;
